import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Newspaper } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { PageHeader, Container, Swash } from "@/components/shared/PageHeader";
import { FadeUp } from "@/components/shared/FadeUp";
import { PostCard } from "@/components/shared/PostCard";
import { NewsletterSignup } from "@/components/shared/NewsletterSignup";
import { Seo } from "@/components/shared/Seo";
import { fetchPosts } from "@/lib/api";
import { cn } from "@/lib/utils";

/** /nytt – the house's own news: the latest story opens big, the rest in a three-column grid, tags to filter (?stikkord=). */
export default function News() {
  const [params, setParams] = useSearchParams();
  const tag = params.get("stikkord") || null;
  const setTag = (t) => setParams(t ? { stikkord: t } : {}, { replace: true });
  const { data, isLoading } = useQuery({ queryKey: ["posts", tag], queryFn: () => fetchPosts(tag ? { tag } : undefined) });
  const items = data?.items || [];
  const [first, ...rest] = items;
  return (
    <div data-testid="news-page">
      <Seo title={tag ? `Nytt frå huset – ${tag}` : "Nytt frå huset"} description="Siste nytt frå Halsnøy Kulturhus – konsertar, dugnad, prosjekt og folk på huset." og={{ kind: "side", id: "nytt" }} path="/nytt" />
      <PageHeader eyebrow="Nytt frå huset" title={<Swash text="Det som skjer *bak* og på scena" />} intro="Små og store saker frå huset: nye arrangement, dugnad, prosjekt og folka som får det til å skje.">
        {(data?.tags?.length || 0) > 0 && (
          <div className="mt-8 flex flex-wrap gap-2" data-testid="news-tags">
            <button type="button" onClick={() => setTag(null)} className={cn("h-9 rounded-full border px-4 text-[13.5px] font-medium transition-colors", !tag ? "border-foreground bg-foreground text-background" : "border-border bg-card hover:border-foreground")} data-testid="news-tag-all">Alle</button>
            {data.tags.map((t) => <button key={t.tag} type="button" onClick={() => setTag(t.tag === tag ? null : t.tag)} className={cn("h-9 rounded-full border px-4 text-[13.5px] font-medium capitalize transition-colors", tag === t.tag ? "border-foreground bg-foreground text-background" : "border-border bg-card hover:border-foreground")} data-testid={`news-tag-${t.tag}`}>{t.tag} <span className="ml-1 text-muted-foreground tabular">{t.count}</span></button>)}
          </div>
        )}
      </PageHeader>
      <Container className="pb-16 sm:pb-20">
        {isLoading ? (
          <div className="grid gap-6 lg:grid-cols-12"><Skeleton className="aspect-[16/10] rounded-[20px] lg:col-span-8" /><div className="space-y-3 lg:col-span-4"><Skeleton className="h-4 w-32" /><Skeleton className="h-12 w-full" /><Skeleton className="h-20 w-full" /></div></div>
        ) : !items.length ? (
          <div className="flex flex-col items-center rounded-[20px] border border-dashed border-border py-20 text-center" data-testid="news-empty">
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-secondary text-muted-foreground"><Newspaper className="h-5 w-5" /></span>
            <h2 className="t-h3 mt-5">{tag ? `Ingen saker med stikkordet «${tag}»` : "Ingen saker enno"}</h2>
            <p className="mt-2 max-w-sm text-[15px] text-muted-foreground">{tag ? "Prøv eit anna stikkord, eller sjå alle sakene." : "Dei første sakene frå huset kjem snart. Meld deg på nyheitsbrevet, så får du dei rett i innboksen."}</p>
            {tag && <button type="button" onClick={() => setTag(null)} className="mt-6 h-10 rounded-full border border-foreground px-5 text-[14px] font-medium transition-colors hover:bg-foreground hover:text-background" data-testid="news-empty-all">Alle saker</button>}
          </div>
        ) : (
          <>
            <FadeUp><PostCard post={first} featured testId="news-featured" /></FadeUp>
            {rest.length > 0 && (
              <div className="mt-16 grid gap-x-8 gap-y-12 border-t border-border pt-12 sm:grid-cols-2 lg:grid-cols-3" data-testid="news-grid">
                {rest.map((p, i) => <FadeUp key={p.id} delay={Math.min(i, 5) * 0.05}><PostCard post={p} /></FadeUp>)}
              </div>
            )}
          </>
        )}
      </Container>
      <section className="grain bg-ink py-16 text-paper sm:py-20" data-testid="news-newsletter">
        <Container>
          <div className="grid gap-8 lg:grid-cols-12 lg:items-center">
            <div className="lg:col-span-6"><p className="t-mono text-paper/55">Nyheitsbrev</p><h2 className="t-h2 mt-3 text-balance text-paper">Få nytt frå huset rett i innboksen</h2><p className="mt-4 max-w-md text-[16px] leading-relaxed text-paper/65">Nye arrangement, dugnad og små gledar – samla i eit nyheitsbrev når det er noko å melde.</p></div>
            <div className="lg:col-span-5 lg:col-start-8"><NewsletterSignup source="program" testId="news-newsletter" /></div>
          </div>
        </Container>
      </section>
    </div>
  );
}
